import { useEffect, useState } from "react";

import { LabelMatch, PassioFood } from "./types";
import type { CatalogApi } from "./useCatalogApi";

/**
 * Compute auto-matched label suggestions for an existing food (by name/brand) so
 * the live LabelLink can offer them as one-tap "add" chips. Suggestions are NOT
 * auto-linked here (these are existing curated records) — the reviewer taps to
 * add. Re-runs when the selected food's id/name/brand changes.
 */
export const useLabelSuggestions = (
  api: Pick<CatalogApi, "autoMatchLabels">,
  food: PassioFood | null | undefined
): LabelMatch[] => {
  const [suggestions, setSuggestions] = useState<LabelMatch[]>([]);
  const name = String(food?.name || "").trim();
  const brand = String(food?.brandName || food?.branded?.owner || "").trim();
  const id = String(food?.id || "");

  useEffect(() => {
    if (!name && !brand) {
      setSuggestions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await api.autoMatchLabels([{ name, brand }]);
        if (!cancelled) setSuggestions(res[0]?.matches || []);
      } catch {
        if (!cancelled) setSuggestions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [api, id, name, brand]);

  return suggestions;
};
